import Icon from "@mui/material/Icon";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
} from "material-react-table";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Theme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useCallback, useMemo, useState } from "react";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import {
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "./fee-schedule-table-cell";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
}));
const MobileToolbarButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));

type FeeScheduleCategoriesReadonlyProps = {
  className?: string;
  categories: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
};

const columnHelper = createMRTColumnHelper<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>();

type CategoryNameCellProps = {
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
};

function CategoryNameCell({ category }: Readonly<CategoryNameCellProps>) {
  const value = getCategoryNameValue(category);
  return <FeeScheduleTableCellText emphasized>{value}</FeeScheduleTableCellText>;
}

function renderCategoryNameCell({ row }: { row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency } }) {
  return <CategoryNameCell category={row.original} />;
}

function CategoryDescriptionCell({ category }: Readonly<CategoryNameCellProps>) {
  const value = getCategoryDescriptionValue(category);
  return <FeeScheduleTableCellText>{value}</FeeScheduleTableCellText>;
}

function renderCategoryDescriptionCell({ row }: { row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency } }) {
  return <CategoryDescriptionCell category={row.original} />;
}

type CategoryFeeCellProps = {
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  viewingAsVendor?: boolean;
};

function CategoryFeeCell({ category, viewingAsVendor }: Readonly<CategoryFeeCellProps>) {
  const feeValue = getCategoryFeeValue(category, viewingAsVendor);
  return feeValue.startsWith("Deferred to ") ? (
    <FeeScheduleTableCellText color="warning">{feeValue}</FeeScheduleTableCellText>
  ) : (
    <FeeScheduleTableCellText>{feeValue}</FeeScheduleTableCellText>
  );
}

function renderCategoryFeeCell(
  {
    row,
  }: {
    row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency };
  },
  viewingAsVendor?: boolean,
) {
  return <CategoryFeeCell category={row.original} viewingAsVendor={viewingAsVendor} />;
}

function CategoryUnitCell({ category }: Readonly<CategoryNameCellProps>) {
  const unitValue = getCategoryUnitValue(category);
  return unitValue ? <FeeScheduleTableCellText>{unitValue}</FeeScheduleTableCellText> : <Icon>remove</Icon>;
}

function renderCategoryUnitCell({ row }: { row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency } }) {
  return <CategoryUnitCell category={row.original} />;
}

function CategoryBillingCodeCell({ category }: Readonly<CategoryNameCellProps>) {
  const billingCodeValue = getCategoryBillingCodeValue(category);
  return billingCodeValue ? <FeeScheduleTableCellText>{billingCodeValue}</FeeScheduleTableCellText> : <Icon>remove</Icon>;
}

function renderCategoryBillingCodeCell({ row }: { row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency } }) {
  return <CategoryBillingCodeCell category={row.original} />;
}

export default function ReadonlyFeeScheduleCategoriesTable(props: Readonly<FeeScheduleCategoriesReadonlyProps>) {
  const { className, categories } = props;

  const session = useSession();
  const isMdDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isXlDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = useMemo(() => [
    columnHelper.accessor(getCategoryNameValue, {
      header: "Name",
      id: "name",
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: renderCategoryNameCell,
    }),
    columnHelper.accessor(getCategoryDescriptionValue, {
      header: "Description",
      id: "description",
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: renderCategoryDescriptionCell,
    }),
    columnHelper.accessor(getCategoryFixedFeeValue, {
      header: "Fixed?",
      id: "fixedFee",
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      minSize: 100,
      size: 100,
    }),
    columnHelper.accessor(
      (category) => getCategoryFeeValue(category, session.context?.viewingAsVendor),
      {
        header: "Fee",
        id: "fee",
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: true,
        filterFn: "includesString",
        minSize: 90,
        size: 90,
        Cell: (cell) => renderCategoryFeeCell(cell, session.context?.viewingAsVendor),
      }
    ),
    columnHelper.accessor(getCategoryUnitValue, {
      header: "Per",
      id: "unit",
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      minSize: 90,
      size: 90,
      Cell: renderCategoryUnitCell,
    }),
    columnHelper.accessor(getCategoryBillingCodeValue, {
      header: "Code",
      id: "billingCode",
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      minSize: 100,
      size: 100,
      Cell: renderCategoryBillingCodeCell,
    }),
  ], [session.context?.viewingAsVendor]);

  const resetToFirstPage = useCallback(() => {
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  }, []);

  const handleColumnFiltersChange = useCallback((updater: MRT_Updater<ColumnFiltersState>) => {
    resetToFirstPage();
    setColumnFilters(updater);
  }, [resetToFirstPage]);

  const handleGlobalFilterChange = useCallback((updater: MRT_Updater<string>) => {
    resetToFirstPage();
    setGlobalFilter(updater);
  }, [resetToFirstPage]);

  const handleSortingChange = useCallback((updater: MRT_Updater<SortingState>) => {
    resetToFirstPage();
    setSorting(updater);
  }, [resetToFirstPage]);

  const renderToolbarInternalActions = useCallback<
    NonNullable<MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>["renderToolbarInternalActions"]>
  >(
    ({ table }) => (
      <>
        {!isMdDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={{ mr: 5, mt: 1 }} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    [isMdDisplaySize],
  );

  const renderTopToolbarCustomActions = useCallback(
    (toolbarProps: { table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> }) => (
      <>
        {isMdDisplaySize && (
          <MobileToolbarButtonsContainer>
            <GlobalFilterTextField table={toolbarProps.table} />
            <ToolbarButtons>
              <ToggleFiltersButton table={toolbarProps.table} />
              <ShowHideColumnsButton table={toolbarProps.table} />
              <ToggleFullScreenButton table={toolbarProps.table} />
            </ToolbarButtons>
          </MobileToolbarButtonsContainer>
        )}
      </>
    ),
    [isMdDisplaySize],
  );
  
  const tableOptions: MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> = {
    columns,
    data: categories,
    // Read-only by construction; the editable twin is `fee-schedule-templates-table`.
    enableRowActions: false,
    enableRowSelection: false,
    enableTableHead: true,
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
    },
    enableColumnPinning: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableGlobalFilterRankedResults: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    globalFilterFn: "includesString",
    layoutMode: "grid",
    manualFiltering: false,
    manualPagination: false,
    manualSorting: false,
    muiTableBodyRowProps: () => ({
      sx: {
        pointerEvents: "none",
      },
    }),
    muiTableBodyCellProps: ({ cell }) => ({
      sx: (theme) => feeScheduleTableCellStyles(theme, { columnId: cell.column.id }),
    }),
    muiSearchTextFieldProps: {
      placeholder: "Search",
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: globalFilter ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                onClick={() => setGlobalFilter("")}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minWidth: 0,
        position: "relative",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        "& .Mui-TableHeadCell-Content": {
          minWidth: 0,
        },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          overflow: "visible",
          textOverflow: "clip",
          whiteSpace: "normal",
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: {
      sx: {
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        "& > .MuiBox-root": {
          gap: 0,
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onPaginationChange: setPagination,
    onSortingChange: handleSortingChange,
    positionGlobalFilter: "none",
    renderEmptyRowsFallback: emptyRowsFallback({
      subject: "fee schedule categories",
    }),
    state: {
      columnFilters,
      density: isXlDisplaySize ? "compact" : "comfortable",
      globalFilter,
      pagination,
      showGlobalFilter: true,
      sorting,
    },
  }

  return (
    <div className={className}>
      <MRTNoMemo<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> options={tableOptions} />
    </div>
  );
}

function getCategoryNameValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.name.value;
}

function getCategoryDescriptionValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.description?.value ?? "";
}

function getCategoryFixedFeeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category?.fee?.unit ? "No" : "Yes";
}

function getCategoryFeeValue(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  viewingAsVendor?: boolean,
): string {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  if (!category.fee) {
    const deferredTo = viewingAsVendor === true ? "client" : "vendor";
    return `Deferred to ${deferredTo}`;
  }

  return new Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency: category.fee?.rate?.currency.toString() ?? "USD",
  }).format(category.fee?.rate?.amount ?? 0);
}

function getCategoryUnitValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category.fee?.unit ?? "";
}

function getCategoryBillingCodeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.billingCode?.value ?? "";
}
